import { useRef } from 'react';
import { useDynamicList, useSubmit, SubmitResult } from '@shopify/react-form';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import { GENERIC_SERVER_ERROR } from '@sdk/login/constants';
import { OnboardingTrialStepsType } from '@sdk/authentication/types';

import type { UpdateWages } from '@web/onboarding/Checklist/onboardingChecklist.types';
import type { Position } from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.types';
import { getSubmitError } from '@web/forms/utils/getSubmitError';
import {
  emptyPositionFactory,
  getWagesPayloadFromPositionsFields,
  getWageError,
} from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.utils';
import { useCompleteStep } from '@web/onboarding/Checklist/hooks/useCompleteStep';
import useStepsConfig from '@web/onboarding/Checklist/hooks/useStepsConfig';
import { decorateSubmit } from '@web/forms/utils/decorateSubmit';
import { FormSubmitStatuses } from '@web/forms/types';

const useDefineWagesForPositionsForm = (positions: Position[], updateWages: UpdateWages) => {
  const api = useSlingApi();
  const completeStep = useCompleteStep();
  const { availableSteps } = useStepsConfig();

  const { fields } = useDynamicList(
    {
      list: positions,
      validates: {
        wage: [getWageError],
      },
    },
    emptyPositionFactory
  );

  const submitPromise = useRef<Promise<SubmitResult>>();
  const {
    submit: formSubmit,
    errors,
    setErrors,
  } = useSubmit(
    async ({ positions }) => {
      const hasSpecifiedAtLeastOneWage = positions.some(({ wage }) => Boolean(Number(wage)));

      if (!hasSpecifiedAtLeastOneWage) {
        submitPromise.current = getSubmitError(gettext('Please specify at least one wage'));
        return submitPromise.current;
      }

      const wagesToSave = getWagesPayloadFromPositionsFields(positions);
      const wagesUpdatePromises = wagesToSave.map((positionWage) =>
        api.laborCost.updatePositionWages([positionWage])
      );
      const positionsUpdatePromises = positions
        .filter((position) => Boolean(position.wage))
        .map(({ id, name, color }) =>
          api.positions.updatePosition({
            id,
            name,
            color,
            type: 'position',
          })
        );

      try {
        const results = await Promise.all(wagesUpdatePromises);
        await Promise.all(positionsUpdatePromises);
        results.forEach(({ data }) => {
          updateWages({ wages: data });
        });
        completeStep(OnboardingTrialStepsType.defineWages, availableSteps);
      } catch (e) {
        submitPromise.current = getSubmitError(e.data?.message || GENERIC_SERVER_ERROR);
        return submitPromise.current;
      }

      submitPromise.current = Promise.resolve({ status: FormSubmitStatuses.success });
      return submitPromise.current;
    },
    { positions: fields }
  );

  const submit = decorateSubmit(formSubmit, submitPromise);

  return {
    fields,
    submit,
    errors,
    setErrors,
  };
};

export default useDefineWagesForPositionsForm;
